import QuestionAnswerComponent, {Question} from "../components/help/QuestionAnswerComponent.tsx";
import {DispatchHighLightAddressSearchEvent} from "../util/events.ts";
import {Link} from "react-router-dom";
import Routing from "../Routing.ts";
import Icon from "../components/selfcare/Icon.tsx";
import {imageMap} from "../images/imageMap.ts";
import TrustBox from "../components/trustpilot/trustBoxComponent.tsx";
import {useEffect, useState} from "react";
import {shouldShowPayerDayWarning, shouldShowWinterHolidayWarning} from "../util/helpers.ts";
import ContactDecemberClosedDays from "../components/contact/ContactDecemberClosedDays.tsx";
import ContactPrayerDayClosed from "../components/contact/ContactPrayerDayClosed.tsx";
import CallMeRTOComponent from "../components/callMe/CallMeRTOComponent.tsx";
import MVFP2025HeroComponent from "../components/hero/MVFP2025HeroComponent.tsx";
import ProductShowcaseComponent from "../components/broadband/ProductShowcaseComponent.tsx";
import ScrollToTop from "../components/ScrollToTop.tsx";
import useDatalayer from "../components/useDatalayer.tsx";
import setTitleAndMetaDescription from "../components/setTitleAndMetaDescription.tsx";
import CallMeFloatingComponent from "../components/callMe/CallMeFloatingComponent.tsx";
import ChatBotBoxComponent from "../components/ChatBotBoxComponent.tsx";
import IconLookingGlass from "../components/icons/IconLookingGlass.tsx";
import IconDocument from "../components/icons/IconDocument.tsx";
import IconRouterSmall from "../components/icons/IconRouterSmall.tsx";
import IconInfo from "../components/icons/IconInfo.tsx";
import IconCustomerService from "../components/icons/IconCustomerService.tsx";

const questions: Question[] = [
    {
        question: 'Hvilket internet kan jeg få?',
        answer: <>
            <p>
                Det internetabonnement som du kan købe, afhænger af din adresse og hvilket internetnetværk, som er i dit område.
                Når du laver et adresseopslag, kan du se hvilke internetforbindelser og internetpriser, som du kan vælge imellem.
            </p>
            <p>
                Lav et <button className={"link"}
                onClick={() => DispatchHighLightAddressSearchEvent(true)}
            >adresseopslag her</button> for at se det billigste internettilbud.
            </p>
        </>,
    },
    {
        question: 'Hvilket internet skal jeg vælge?',
        answer: <p>
            Hiper anbefaler altid at vælge <Link to={Routing.Broadband.Fiber}>fibernet</Link>, hvis du har mulighed for dette på din adresse.
            Det er den bedste internetforbindelse, som du kan få, og det er en god fremtidssikring af dit internet.
            Du kan dog også få internet via dit <Link to={Routing.Broadband.Coax}>kabel-tv-stik</Link> eller <Link to={Routing.Broadband.TDC5G}>5G-internet</Link>, og hvis du vil blive lidt klogere på, hvad der adskiller de forskellige forbindelser fra hinanden, så læs artiklen <Link
            to="/hjaelp/internet-og-wifi/hvilket-internet-skal-jeg-vaelge"
        >sammenlign internetforbindelser</Link>.
        </p>,
    },
    {
        question: 'Hvad koster internet om måneden?',
        answer: <>
            <p>
                Din månedlige internetpris afhænger af hvilken type <Link to={Routing.Broadband.Index}>bredbånd</Link>, som du kan få på din adresse.
                Lav gerne et adresseopslag for at se, hvilket internettilbud som du kan købe.
                Vi tilbyder altid det billigste og bedste internettilbud til din adresse.
            </p>
            <p>
                <button className={"link"} onClick={() => DispatchHighLightAddressSearchEvent(true)}>Bestil internet her</button>
                &ndash; vi har nogle af de bedste internetpriser i markedet.
            </p>
        </>,
    },
]

export default function IndexPage() {
    ScrollToTop()
    useDatalayer()
    setTitleAndMetaDescription('Internet fra Hiper | Lynhurtigt 1000 Mbit fra 99,- mdr.', 'Køb internet fra Hiper og få en lynhurtig og stabil internetforbindelse til en knivskarp pris. Hos Hiper får du mest værdi for pengene.')

    const [trustScore, setTrustScore] = useState<string | null>()

    useEffect(() => {
        fetch(`/trustpilot-score`).then((r) => {
            r.json().then((j) => {
                setTrustScore(j.trustscore)
            })
        }).catch(() => {})
    }, [])

    return (
        <div className={"page-home"}>
            <MVFP2025HeroComponent/>

            <CallMeRTOComponent className={"rto--box page-home"}/>

            <div className="products-head">
                <h2 className="heading heading--montserrat heading--bigger">
                    Få Lynhurtigt internet
                </h2>
                <h3 className="heading heading--paragraph heading--small">
                    Uanset om du foretrækker mobilt 5G-internet eller en fast forbindelse, har vi altid en løsning, der passer til dig!
                </h3>
            </div>

            <ProductShowcaseComponent/>

            <section className="content content--wide section-white">
                <div className="header-steps">
                    <h2 className="heading heading--blue heading--big">
                        Så nemt er det at få internet fra Hiper
                    </h2>
                    <h3 className="heading heading--paragraph heading--small">
                        Hos Hiper har vi gjort det nemt for alle at få lynhurtigt og billigt internet &ndash; lige fra bestilling af nyt til opsigelse af gammel internetudbyder.
                    </h3>
                </div>
                <div className="grid--steps">
                    <div className="grid--item">
                        <span className="number">1</span>
                        <Icon component={IconLookingGlass}/>
                        <h4 className="heading heading--paragraph heading--small">
                            <span>Indtast din adresse</span>
                            <span>og bestil nemt dit nye internet</span>
                        </h4>
                    </div>

                    <div className="grid--item">
                        <span className="number">2</span>
                        <Icon component={IconDocument}/>
                        <h4 className="heading heading--paragraph heading--small">
                            <span>Vi hjælper med at opsige</span>
                            <span>hos din gamle internetudbyder</span>
                        </h4>
                    </div>

                    <div className="grid--item">
                        <span className="number">3</span>
                        <Icon component={IconRouterSmall}/>
                        <h4 className="heading heading--paragraph heading--small">
                            <span>Du modtager dit udstyr</span>
                            <span>&ndash; så er du i gang med dit hurtige internet</span>
                        </h4>
                    </div>
                </div>
            </section>

            <section className="trustpilot-header">
                <div className="blurb">
                    <h2 className="heading heading--white heading--medium">Hvad siger vores kunder om os?</h2>
                    <p>
                        Vi arbejder hver dag for, at vores kunder i hele landet er tilfredse med deres internetforbindelse.
                    </p>
                    {trustScore !== undefined && trustScore !== null && (
                        <p>
                            Vi har <span className="star-count"><strong>{trustScore}</strong></span> ud af 5 stjerner på Trustpilot.
                        </p>
                    )}
                    <p>
                        Derudover er vi også rangeret <strong>nr.&nbsp;2</strong> blandt 17 internetudbydere på Trustpilot.
                    </p>
                </div>
                <div className="arrow-container">
                    <span className="arrow-down"></span>
                </div>
            </section>

            <TrustBox templateId={"53aa8912dec7e10d38f59f36"} extraClassName={"content content--wide"}/>

            <section className="section-teal">
                <div className="content">
                    <div className="blurb">
                        <h2 className="heading heading--white heading--big">
                            Hiper internet
                        </h2>
                        <p className="align-center">
                            Vi udbyder kun internet, og vi er rigtig gode til det.
                            Vi er en af de bedste internetudbydere på markedet og er stålsatte på at levere hurtigt internet til de bedste og billigste priser.
                        </p>
                    </div>
                </div>
            </section>

            <div className="usp-container">
                <div className="usp-image">
                    <img
                        src={imageMap.home.usp_container}
                        alt="Hiper er vinder af Mest Værdi For Pengene i 2020, 2021, 2022, 2023, 2024 og 2025 - for bedste internet."
                    />
                </div>
                <div className="usp-content">
                    <h2>Mest værdi for pengene</h2>
                    <h3><span className="hiper-text-stroke">Vinder</span> 6. år i&nbsp;træk</h3>
                    <p>
                        Vinder af &ldquo;Mest Værdi for Pengene&rdquo; af Loyalty Groups BrancheIndex Internet 2020, 2021, 2022, 2023, 2024 og 2025.
                    </p>
                    <p>
                        Vi har vundet &ldquo;Mest værdi for pengene&rdquo; &ndash;
                        og for sjette år i træk giver vi alle vores kunder mest værdi for pengene blandt alle internetudbydere i 2020, 2021, 2022, 2023, 2024 og 2025.
                        Undersøgelsen er din garanti for, at du får den bedste internetløsning på markedet &ndash;
                        målt på både hastighed og pris.
                    </p>
                    <Link to={Routing.About.Index} className="button button--montserrat red rounded">Læs mere</Link>
                </div>
            </div>

            <section className="section-about-internet section-white">
                <div className="content content--slim">
                    <div className="blurb">
                        <h2 className="heading heading--big">
                            Om internet
                        </h2>
                    </div>

                    <QuestionAnswerComponent questions={questions}/>
                </div>
            </section>

            <section className="contact contact--simple">
                <h2 className="heading heading--medium">Har du brug for hjælp til dit internet?</h2>
                <p>
                    Søg i vores <Link to={Routing.Help.Index}>hjælpeunivers</Link> og find hurtigt svar omkring dit internet.
                    Du har også mulighed for at ringe eller chatte med os.
                </p>
                <div className="grid grid--contact">
                    <Link to={Routing.Help.Index} className="grid-item grid-item--contact">
                        <Icon component={IconInfo}/>
                        <div className="text">
                            <p>Hjælpeunivers</p>
                            <p>Guides &amp; Artikler</p>
                        </div>
                    </Link>
                    <Link to={Routing.Contact.Index} className="grid-item grid-item--contact">
                        <Icon component={IconCustomerService}/>
                        <div className="text">
                            <p>Kontakt os</p>
                        </div>
                    </Link>
                </div>

                {shouldShowWinterHolidayWarning() && (
                    <ContactDecemberClosedDays/>
                )}

                {shouldShowPayerDayWarning() && (
                    <ContactPrayerDayClosed/>
                )}
            </section>

            <CallMeFloatingComponent/>
            <ChatBotBoxComponent showAfterScroll={true} />
        </div>
    )
}
